import classNames from 'classnames'
import { forEach, groupBy, take } from 'lodash'
import { create } from 'react-modal-promise'

import AccountChartsModal from '../components/AccountChartsModal/AccountChartsModal'
import { appApi } from '../api/app.api'
import { formatDate } from './helper.util'
import { parseAndRound, round } from './calc.util'

const HOUR_IN_SECONDS = 60 * 60

const getSeriesTime = (time) => time / 1000

const getOrderMarkerTime = (time) => getSeriesTime(time) - HOUR_IN_SECONDS

const getSimpleSeriesData = (klines, indicatorsData, indicatorResponseName, index) => {
  return indicatorsData[indicatorResponseName][index].map((value, index) => {
    return {
      time: getSeriesTime(klines.OPEN_TIME[index]),
      value: parseAndRound(value, 4)
    }
  })
}

const getFiniteSeriesData = (seriesData) => {
  return seriesData.filter((seriesItem) => Number.isFinite(seriesItem?.value))
}

const getAccountRateValue = (account, rateName, fallbackValue = null) => {
  return account?.rates?.find((rate) => rate.name === rateName)?.value ?? fallbackValue
}

const getKlineStepInSeconds = (klines) => {
  const openTimes = klines?.OPEN_TIME ?? []

  if (openTimes.length < 2) {
    return HOUR_IN_SECONDS
  }

  const lastStep = Math.round((openTimes[openTimes.length - 1] - openTimes[openTimes.length - 2]) / 1000)

  return Math.max(lastStep, 1)
}

const getBBRegressionMarkers = (klines, upperRejectSignalData, lowerReclaimSignalData) => {
  return klines.OPEN_TIME.reduce((markers, openTime, index) => {
    const time = getSeriesTime(klines.OPEN_TIME[index])

    if (upperRejectSignalData?.[index] > 0) {
      markers.push({
        time,
        position: 'aboveBar',
        shape: 'arrowDown',
        color: '#ff6b6b',
        text: 'BBRF'
      })
    }

    if (lowerReclaimSignalData?.[index] > 0) {
      markers.push({
        time,
        position: 'belowBar',
        shape: 'arrowUp',
        color: '#14b8a6',
        text: 'BBRF'
      })
    }

    return markers
  }, [])
}

const buildBBRegressionForecastSeries = (
  klines,
  upperData,
  basisData,
  lowerData,
  basisSlopeData,
  deviationSlopeData,
  minimumWidthData,
  forecastBars
) => {
  const lastIndex = basisData.length - 1

  if (lastIndex < 0) {
    return {
      forecastUpperSeriesData: [],
      forecastBasisSeriesData: [],
      forecastLowerSeriesData: []
    }
  }

  const lastUpper = upperData[lastIndex]
  const lastBasis = basisData[lastIndex]
  const lastLower = lowerData[lastIndex]
  const lastBasisSlope = basisSlopeData[lastIndex]
  const lastDeviationSlope = deviationSlopeData[lastIndex]
  const lastMinimumWidth = minimumWidthData[lastIndex]
  const lastTime = getSeriesTime(klines.OPEN_TIME[lastIndex])
  const stepInSeconds = getKlineStepInSeconds(klines)

  if (![lastUpper, lastBasis, lastLower, lastBasisSlope, lastDeviationSlope, lastMinimumWidth, lastTime].every(Number.isFinite)) {
    return {
      forecastUpperSeriesData: [],
      forecastBasisSeriesData: [],
      forecastLowerSeriesData: []
    }
  }

  const lastDeviation = lastUpper - lastBasis
  const safeForecastBars = Math.max(1, Math.round(forecastBars))
  const forecastUpperSeriesData = []
  const forecastBasisSeriesData = []
  const forecastLowerSeriesData = []

  for (let step = 0; step <= safeForecastBars; step++) {
    const weight = step / safeForecastBars
    const projectedBasis = lastBasis + lastBasisSlope * step
    const projectedDeviation = Math.max(lastDeviation + lastDeviationSlope * step, lastMinimumWidth)
    const projectedUpper = projectedBasis + projectedDeviation
    const projectedLower = projectedBasis - projectedDeviation
    const time = lastTime + stepInSeconds * step
    const smoothUpper = lastUpper * (1 - weight) + projectedUpper * weight
    const smoothBasis = lastBasis * (1 - weight) + projectedBasis * weight
    const smoothLower = lastLower * (1 - weight) + projectedLower * weight

    forecastUpperSeriesData.push({ time, value: parseAndRound(smoothUpper, 4) })
    forecastBasisSeriesData.push({ time, value: parseAndRound(smoothBasis, 4) })
    forecastLowerSeriesData.push({ time, value: parseAndRound(smoothLower, 4) })
  }

  return {
    forecastUpperSeriesData,
    forecastBasisSeriesData,
    forecastLowerSeriesData
  }
}

export const getMainChartSeries = (klines, account, indicatorsData) => {
  const seriesData = klines.OPEN_TIME.map((openTime, index) => {
    return {
      time: getSeriesTime(openTime),
      open: parseAndRound(klines.OPEN[index], 4),
      close: parseAndRound(klines.CLOSE[index], 4),
      high: parseAndRound(klines.HIGH[index], 4),
      low: parseAndRound(klines.LOW[index], 4),
      color: klines.OPEN[index] > klines.CLOSE[index] ? '#ef5350' : '#26a699'
    }
  })

  let markers = []

  if (indicatorsData.signalsResponse?.[0]) {
    markers = indicatorsData.signalsResponse[0].map((signal, index) => {
      const isBuySignal = signal === 0
      const isNullSignal = signal === 2

      return {
        time: getSeriesTime(klines.OPEN_TIME[index]),
        position: isNullSignal ? '' : isBuySignal ? 'belowBar' : 'aboveBar',
        shape: isNullSignal ? '' : isBuySignal ? 'arrowUp' : 'arrowDown',
        color: isNullSignal ? '' : isBuySignal ? 'green' : 'red',
        text: isNullSignal ? '' : signal
      }
    })
  } else {
    markers = account.spot.closedOrders.reduce((acc, order) => {
      const isLongOrder = order.type === 'SELL'
      acc.push({
        time: getOrderMarkerTime(order.buyingDate),
        position: isLongOrder ? 'belowBar' : 'aboveBar',
        shape: isLongOrder ? 'arrowUp' : 'arrowDown',
        color: isLongOrder ? 'deepskyblue' : 'violet'
      })

      const isSuccessfulOrder = isLongOrder ? order.sellingPrice > order.buyingPrice : order.sellingPrice < order.buyingPrice

      acc.push({
        time: getOrderMarkerTime(order.sellingDate),
        position: isSuccessfulOrder ? 'aboveBar' : 'belowBar',
        shape: isSuccessfulOrder ? 'arrowDown' : 'arrowUp',
        color: isSuccessfulOrder ? 'lime' : 'red',
        text: String(order.priceChangePercentage)
      })
      return acc
    }, [])

  }

  if (indicatorsData.bbRegressionForecastIndicatorResponse) {
    const [, , , , , , , , , upperRejectSignalData = [], lowerReclaimSignalData = []] =
      indicatorsData.bbRegressionForecastIndicatorResponse

    markers = markers.concat(getBBRegressionMarkers(klines, upperRejectSignalData, lowerReclaimSignalData))
  }

  markers.sort((a, b) => a.time - b.time)

  return { seriesData, markers, name: 'candlestick', type: 'Candlestick', options: {} }
}

export const getBBLowSeries = (klines, indicatorsData) => {
  if (!indicatorsData.bbIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'bbIndicatorResponse', 0)

  return { seriesData, name: 'bbLow', type: 'Line', options: { color: 'tomato', lineWidth: 1 } }
}

export const getBBHighSeries = (klines, indicatorsData) => {
  if (!indicatorsData.bbIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'bbIndicatorResponse', 2)

  return {
    seriesData, name: 'bbHigh', type: 'Line', options: {
      color: 'palegreen',
      lineWidth: 1
    }
  }
}

export const getBBRegressionOverlaySeries = (klines, indicatorsData, account) => {
  if (!indicatorsData.bbRegressionForecastIndicatorResponse) return []

  const [
    upperData = [],
    basisData = [],
    lowerData = [],
    ,
    forecastBasisData = [],
    ,
    basisSlopeData = [],
    deviationSlopeData = [],
    minimumWidthData = [],
    upperRejectSignalData = [],
    lowerReclaimSignalData = []
  ] = indicatorsData.bbRegressionForecastIndicatorResponse
  const forecastBars = getAccountRateValue(account, 'bbRegressionForecastBarsRate', 40)
  const upperSeriesData = getFiniteSeriesData(upperData.map((value, index) => ({
    time: getSeriesTime(klines.OPEN_TIME[index]),
    value: parseAndRound(value, 4)
  })))
  const lowerSeriesData = getFiniteSeriesData(lowerData.map((value, index) => ({
    time: getSeriesTime(klines.OPEN_TIME[index]),
    value: parseAndRound(value, 4)
  })))
  const basisSeriesData = getFiniteSeriesData(basisData.map((value, index) => ({
    time: getSeriesTime(klines.OPEN_TIME[index]),
    value: parseAndRound(value, 4),
    color: basisData[index] > (basisData[index - 2] ?? basisData[index]) ? '#14b8a6' : '#ef4444'
  })))
  const { forecastUpperSeriesData, forecastBasisSeriesData, forecastLowerSeriesData } = buildBBRegressionForecastSeries(
    klines,
    upperData,
    basisData,
    lowerData,
    basisSlopeData,
    deviationSlopeData,
    minimumWidthData,
    forecastBars
  )
  const isForecastBullish = Number.isFinite(forecastBasisData[forecastBasisData.length - 1]) &&
    Number.isFinite(basisData[basisData.length - 1]) &&
    forecastBasisData[forecastBasisData.length - 1] >= basisData[basisData.length - 1]
  const forecastBasisColor = isForecastBullish ? '#14b8a6' : '#ef4444'

  return [
    {
      seriesData: upperSeriesData,
      name: 'bbRegressionUpper',
      type: 'Line',
      options: { color: 'rgba(0, 137, 123, 0.55)', lineWidth: 1 }
    },
    {
      seriesData: basisSeriesData,
      name: 'bbRegressionBasis',
      type: 'Line',
      options: { color: forecastBasisColor, lineWidth: 2 }
    },
    {
      seriesData: lowerSeriesData,
      name: 'bbRegressionLower',
      type: 'Line',
      options: { color: 'rgba(0, 137, 123, 0.55)', lineWidth: 1 }
    },
    {
      seriesData: forecastUpperSeriesData,
      name: 'bbRegressionForecastUpper',
      type: 'Line',
      options: { color: 'rgba(0, 187, 212, 0.55)', lineWidth: 1 }
    },
    {
      seriesData: forecastBasisSeriesData,
      name: 'bbRegressionForecastBasis',
      type: 'Line',
      options: { color: forecastBasisColor, lineWidth: 2 }
    },
    {
      seriesData: forecastLowerSeriesData,
      name: 'bbRegressionForecastLower',
      type: 'Line',
      options: { color: 'rgba(0, 187, 212, 0.55)', lineWidth: 1 }
    }
  ].filter((seriesItem) => seriesItem.seriesData.length > 0)
}

export const getRSISeries = (klines, indicatorsData, account) => {
  if (!indicatorsData.rsiIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'rsiIndicatorResponse', 0)

  const priceLines = [
    {
      price: 80,
      color: 'palegreen',
      lineWidth: 1,
    },
    {
      price: 20,
      color: 'tomato',
      lineWidth: 1,
    }
  ]

  const rsiRate = account.rates.find((rate) => rate.name === 'rsiRate')

  if (rsiRate) {
    priceLines.push({
      price: rsiRate.value,
      color: 'cyan',
      lineWidth: 1,
    })
  }

  return { seriesData, name: 'rsi', type: 'Line', options: { lineWidth: 2, color: 'cyan' }, priceLines }
}

export const getStochKSeries = (klines, indicatorsData, account) => {
  if (!indicatorsData.stochIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'stochIndicatorResponse', 0)

  const priceLines = [
    {
      price: 80,
      color: 'palegreen',
      lineWidth: 1,
    },
    {
      price: 20,
      color: 'tomato',
      lineWidth: 1,
    }
  ]

  const stochKRate = account.rates.find((rate) => rate.name === 'stochKRate')

  if (stochKRate) {
    priceLines.push({
      price: stochKRate.value,
      color: 'yellow',
      lineWidth: 1,
    })
  }

  return { seriesData, name: 'stochK', type: 'Line', options: { lineWidth: 2, color: 'yellow' }, priceLines }
}

export const getStochDSeries = (klines, indicatorsData) => {
  if (!indicatorsData.stochIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'stochIndicatorResponse', 1)

  return { seriesData, name: 'stochD', type: 'Line', options: { lineWidth: 2, color: 'hotpink' } }
}

export const getWave1Series = (klines, indicatorsData, account) => {
  if (!indicatorsData.wt1IndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'wt1IndicatorResponse', 0)

  const priceLines = [
    {
      price: 80,
      color: 'palegreen',
      lineWidth: 1,
    },
    {
      price: 0,
      color: 'white',
      lineWidth: 1,
    },
    {
      price: -80,
      color: 'tomato',
      lineWidth: 1,
    }
  ]

  const wt1Rate = account.rates.find((rate) => rate.name === 'wt1Rate')

  if (wt1Rate) {
    priceLines.push({
      price: wt1Rate.value,
      color: 'mediumorchid',
      lineWidth: 1,
    })
  }

  return { seriesData, name: 'wave1', type: 'Line', options: { lineWidth: 2, color: 'mediumorchid' }, priceLines }
}

export const getWave2Series = (klines, indicatorsData, account) => {
  if (!indicatorsData.wt1IndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'wt1IndicatorResponse', 1)

  const wt2Rate = account.rates.find((rate) => rate.name === 'wt2Rate')
  const priceLines = []

  if (wt2Rate) {
    priceLines.push({
      price: wt2Rate.value,
      color: 'cyan',
      lineWidth: 1,
    })
  }

  return { seriesData, name: 'wave2', type: 'Line', options: { lineWidth: 2, color: 'cyan' }, priceLines }
}

export const getWave3Series = (klines, indicatorsData, account) => {
  if (!indicatorsData.wt1IndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'wt1IndicatorResponse', 2)

  const wt3Rate = account.rates.find((rate) => rate.name === 'wt3Rate')
  const priceLines = []

  if (wt3Rate) {
    priceLines.push({
      price: wt3Rate.value,
      color: 'palegreen',
      lineWidth: 1,
    })
  }

  return { seriesData, name: 'wave3', type: 'Line', options: { lineWidth: 2, color: 'palegreen' }, priceLines }
}

export const getTrendiloUpSeries = (klines, indicatorsData) => {
  if (!indicatorsData.trendiloUpIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'trendiloUpIndicatorResponse', 0)
  const rmsPlus = indicatorsData.trendiloUpIndicatorResponse[1]

  seriesData.forEach((seriesItem, index) => {
    if (seriesItem.value > rmsPlus[index]) {
      seriesItem.color = 'green'
    }
  })

  return { seriesData, name: 'trendiloUp', type: 'Line', options: { lineWidth: 2, color: 'white' }}
}

export const getTrendiloDownSeries = (klines, indicatorsData) => {
  if (!indicatorsData.trendiloDownIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'trendiloDownIndicatorResponse', 0)
  const rmsMinus = indicatorsData.trendiloDownIndicatorResponse[2]

  seriesData.forEach((seriesItem, index) => {
    if (seriesItem.value < rmsMinus[index]) {
      seriesItem.color = 'red'
    }
  })

  return { seriesData, name: 'trendiloDown', type: 'Line', options: { lineWidth: 2, color: 'gray' }}
}

export const getTrendiloRmsPlusSeries = (klines, indicatorsData) => {
  if (!indicatorsData.trendiloUpIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'trendiloUpIndicatorResponse', 1)

  return { seriesData, name: 'trendiloRmsPlus', type: 'Line', options: { lineWidth: 1, color: 'cyan' }}
}

export const getTrendiloRmsMinusSeries = (klines, indicatorsData) => {
  if (!indicatorsData.trendiloDownIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'trendiloDownIndicatorResponse', 2)

  return { seriesData, name: 'trendiloRmsMinus', type: 'Line', options: { lineWidth: 1, color: 'cyan' }}
}

export const getVolumeOSeries = (klines, indicatorsData) => {
  if (!indicatorsData.volumeOIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'volumeOIndicatorResponse', 0)

  return { seriesData, name: 'volumeO', type: 'Line', options: { lineWidth: 1, color: 'cyan' }}
}

export const getMACDSeries = (klines, indicatorsData) => {
  if (!indicatorsData.macdIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'macdIndicatorResponse', 0)

  return { seriesData, name: 'macd', type: 'Line', options: { lineWidth: 2, color: 'orange' } }
}

export const getMACDSignalSeries = (klines, indicatorsData) => {
  if (!indicatorsData.macdIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'macdIndicatorResponse', 1)

  return { seriesData, name: 'macdSignal', type: 'Line', options: { lineWidth: 1, color: 'deepskyblue' } }
}

export const getMACDHistogramSeries = (klines, indicatorsData) => {
  if (!indicatorsData.macdIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'macdIndicatorResponse', 2)

  seriesData.forEach((seriesItem, index) => {
    const previousValue = seriesData[index - 1]?.value ?? 0

    if (seriesItem.value >= 0) {
      seriesItem.color = seriesItem.value >= previousValue ? '#6fd08c' : '#2f8f60'
    } else {
      seriesItem.color = seriesItem.value <= previousValue ? '#ef5350' : '#9b2c2c'
    }
  })

  return { seriesData, name: 'macdHistogram', type: 'Histogram' }
}

export const getADXSeries = (klines, indicatorsData, account) => {
  const indicatorName = indicatorsData.adxDiIndicatorResponse
    ? 'adxDiIndicatorResponse'
    : indicatorsData.adxIndicatorResponse
      ? 'adxIndicatorResponse'
      : null

  if (!indicatorName) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, indicatorName, 0)
  const priceLines = []
  const adxThresholdRate = account.rates.find((rate) => rate.name === 'adxThresholdRate')
  const adxTrendRate = account.rates.find((rate) => rate.name === 'adxTrendRate')
  const thresholdRate = adxThresholdRate ?? adxTrendRate

  if (thresholdRate) {
    priceLines.push({
      price: thresholdRate.value,
      color: 'khaki',
      lineWidth: 1
    })
  }

  return { seriesData, name: 'adx', type: 'Line', options: { lineWidth: 1, color: 'khaki' }, priceLines }
}

export const getDIPlusSeries = (klines, indicatorsData) => {
  if (!indicatorsData.adxDiIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'adxDiIndicatorResponse', 1)

  return { seriesData, name: 'diPlus', type: 'Line', options: { lineWidth: 1, color: '#7ae582' } }
}

export const getDIMinusSeries = (klines, indicatorsData) => {
  if (!indicatorsData.adxDiIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'adxDiIndicatorResponse', 2)

  return { seriesData, name: 'diMinus', type: 'Line', options: { lineWidth: 1, color: '#ff7b72' } }
}

export const getATRSeries = (klines, indicatorsData) => {
  if (!indicatorsData.atrIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'atrIndicatorResponse', 0)

  return { seriesData, name: 'atr', type: 'Line', options: { lineWidth: 2, color: 'salmon' } }
}

export const getEMASeries = (klines, indicatorsData) => {
  if (!indicatorsData.emaIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'emaIndicatorResponse', 0)

  return { seriesData, name: 'ema', type: 'Line', options: { lineWidth: 1, color: '#ffd166' } }
}

export const getHMASeries = (klines, indicatorsData) => {
  if (!indicatorsData.hmaIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'hmaIndicatorResponse', 0)

  return { seriesData, name: 'hma', type: 'Line', options: { lineWidth: 2, color: '#06d6a0' } }
}

export const getVWAPSeries = (klines, indicatorsData) => {
  if (!indicatorsData.vwapIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'vwapIndicatorResponse', 0)

  return { seriesData, name: 'vwap', type: 'Line', options: { lineWidth: 1, color: '#118ab2' } }
}

export const getSupertrendSeries = (klines, indicatorsData) => {
  if (!indicatorsData.supertrendIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'supertrendIndicatorResponse', 0)
  const directionData = indicatorsData.supertrendIndicatorResponse[1]

  seriesData.forEach((seriesItem, index) => {
    seriesItem.color = directionData[index] > 0 ? '#7ae582' : '#ff7b72'
  })

  return { seriesData, name: 'supertrend', type: 'Line', options: { lineWidth: 2, color: '#7ae582' } }
}

export const getSquizeMomentumSeries = (klines, indicatorsData) => {
  if (!indicatorsData.squizeMomentumIndicatorResponse) return null

  const seriesData = getSimpleSeriesData(klines, indicatorsData, 'squizeMomentumIndicatorResponse', 0)

  seriesData.forEach((seriesItem, index) => {
    if (seriesItem.value > 0) {
      seriesItem.color = seriesItem.value > seriesData[index - 1]?.value ? 'lightgreen' : 'green'
    } else {
      seriesItem.color = seriesItem.value < seriesData[index - 1]?.value ? 'red' : 'maroon'
    }
  })

  return { seriesData, name: 'sMomentum', type: 'Histogram'}
}

export const getSquizeMomentumSeries2 = (klines, indicatorsData) => {
  if (!indicatorsData.squizeMomentumIndicatorResponse) return null

  const sqzData = indicatorsData.squizeMomentumIndicatorResponse

  const seriesData = sqzData[1].map((item, index) => ({
    time: getSeriesTime(klines.OPEN_TIME[index]),
    value: 0,
    color: item ? 'black' : sqzData[2][index] ? 'gray' : 'blue',
    shape: 'circle'
  }))

  return { seriesData, name: 'sMomentum2', type: 'Line'}
}

const copyToClipboard = (event) => {
  event.stopPropagation()
  navigator.clipboard.writeText(event.target.innerText)
}

const showCharts = async (event, strategy, account, centerTime) => {
  event.stopPropagation()

  const [params, titleArray] = account.rates.reduce((acc, rate) => {
    acc[0][rate.name] = rate.value
    acc[1].push(`${rate.name.replace('Rate', '')}:${rate.value}`)
    return acc
  }, [{}, []])

  const title = `${strategy.symbol} # ${titleArray.join(' | ')}`

  const {
    data: {
      klines,
      indicatorsData,
      dataLength
    }
  } = await appApi.getTestingAccountData(
    strategy.strategyType,
    strategy.symbol,
    strategy.subName,
    params
  )

  await create(AccountChartsModal)( {
    klines,
    indicatorsData,
    title,
    account,
    centerTime
  })
}

const generateAccountHeader = (strategy) => {
  const ratesHeaders = strategy.accounts[0]?.rates.map(rate => {
    return {
      className: 'card-cell', label: rate.name
    }
  }) ?? []

  return [
    { className: 'card-cell card-cell_8', label: 'Name' },
    { className: 'card-cell', label: 'Balance' },
    ...ratesHeaders,
    { className: 'card-cell card-cell_3', label: 'Profit' }
  ]
}

const generateAccountRow = (strategy, account) => {
  const profitClassnames = classNames(
    'card-cell',
    'card-cell_3',
    'zoom-in-cursor',
    account.ui.profit > 0 ? 'card-cell-success' : 'card-cell-danger'
  )
  const ratesRows = account.rates.map(rate => {
    return {
      className: 'card-cell',
      value: round(rate.value)
    }
  })

  return [
    {
      className: 'card-cell card-cell_8 clipboard-cursor',
      value: account.name,
      title: account.name,
      onClick: copyToClipboard
    },
    { className: 'card-cell', value: round(account.spot?.balance, 0) },
    ...ratesRows,
    {
      className: profitClassnames,
      value: round(account.ui.profit, 0) + '%',
      onClick: (event) => showCharts(event, strategy, account)
    }
  ]
}

export const strategiesConfig = {
  default: {
    generateAccountHeader: generateAccountHeader,
    generateAccountRow: generateAccountRow
  }
}

export const generateOrderHeader = () => {
  return [
    { className: 'card-cell card-cell_05', label: 'Type' },
    { className: 'card-cell', label: 'Total balance snapshot' },
    { className: 'card-cell', label: 'Balance snapshot' },
    { className: 'card-cell', label: 'Quantity' },
    { className: 'card-cell', label: 'Buying price' },
    { className: 'card-cell', label: 'Selling price' },
    { className: 'card-cell card-cell_05', label: 'Price % change' },
    { className: 'card-cell card-cell_05', label: 'Balance % change' },
    { className: 'card-cell', label: 'Profit price' },
    { className: 'card-cell', label: 'Stop loss price' },
    { className: 'card-cell', label: 'Buying date' },
    { className: 'card-cell', label: 'Selling date' }
  ]
}

export const generateOrderRow = (order, strategy = null, account = null) => {
  const buyingPrice = round(order.buyingPrice, 4)
  const sellingPrice = round(order.sellingPrice, 4)
  const isLongOrder = order.type === 'SELL'
  const isBigLoss = isLongOrder ? order.priceChangePercentage < -5 : order.priceChangePercentage > 5
  const priceChangePercentageClassname = classNames('card-cell', 'card-cell_05', { 'card-cell-danger-bg': isBigLoss })
  const priceChangePercentageWithZoomClassname = classNames(
    priceChangePercentageClassname,
    'zoom-in-cursor'
  )

  return [
    { className: 'card-cell card-cell_05', value: order.type },
    { className: 'card-cell', value: round(order.totalBalanceSnapshot) ?? 'N/A' },
    { className: 'card-cell', value: round(order.balanceSnapshot) ?? 'N/A' },
    { className: 'card-cell', value: round(order.quantity, 4) },
    { className: 'card-cell', value: buyingPrice },
    { className: 'card-cell', value: sellingPrice ?? 'N/A' },
    {
      className: priceChangePercentageWithZoomClassname,
      value: order.priceChangePercentage ?? 'N/A',
      onClick: (event) => {
        if (!strategy || !account) return
        showCharts(event, strategy, account, order.buyingDate)
      }
    },
    { className: priceChangePercentageClassname, value: order.totalBalanceSnapshotChangePercentage ?? 'N/A' },
    { className: 'card-cell', value: order.profitPrice ? round(order.profitPrice, 4) : 'N/A' },
    { className: 'card-cell', value: order.stopLossPrice ? round(order.stopLossPrice, 4) : 'N/A' },
    { className: 'card-cell', value: formatDate(order.buyingDate) },
    { className: 'card-cell', value: formatDate(order.sellingDate) }
  ]
}

// todo: deprecated method, delete later
const generateBestAccounts = (strategies, fields) => {
  const groupedStrategies = groupBy(strategies, strategy => getStrategyBaseName(strategy.name))
  const groupedCalculatedAccounts = {}

  forEach(groupedStrategies, (groupedStrategiesByCoin, groupName) => {
    const allAccountsByCoin = []
    groupedStrategiesByCoin.forEach(strategy => {
      allAccountsByCoin.push(...strategy.accounts)
    })
    const groupedAccounts = groupBy(allAccountsByCoin, account => account.name)
    groupedCalculatedAccounts[groupName] = []

    forEach(groupedAccounts, groupedAccount => {
      if (groupedAccount.length > 1) {
        const totalProfit = groupedAccount.reduce((acc, singleAccount) => {
          return acc + round((singleAccount.spot?.balance - singleAccount.spot?.initialBalance) * 100 / singleAccount.spot?.initialBalance)
        }, 0)

        const calculatedAccount = {
          totalProfit,
          name: groupedAccount[0].name,
          avarageProfit: totalProfit / groupedAccount.length
        }

        fields.forEach(field => {
          calculatedAccount[field] = groupedAccount[0][field]
        })

        groupedCalculatedAccounts[groupName].push(calculatedAccount)
      }
    })

    groupedCalculatedAccounts[groupName] =
      take(groupedCalculatedAccounts[groupName].sort((a, b) => b.totalProfit - a.totalProfit), 9)
  })

  return groupedCalculatedAccounts
}

export const getStrategyBaseName = strategyName => strategyName.split('_').slice(0, 2).join('_')
